import * as React from 'react'
import { connect } from 'react-redux'
import { State } from '../../model'

import DI from '../../app/di'

import Breadcrumb, { BreadcrumbItem } from '../../components/Breadcrumb'
import {
  Creative,
  CreativeAction,
  CreativeId,
} from '../../interfaces/entities/creative'

import HistoryTag from '../../components/HistoryTag/HistoryTag'

import Editable from '../../components/Editable'
import {
  IFormValidation,
  FormBlueprint,
} from '../../interfaces/components/dynamic-form'
import { ICreativeService } from '../../interfaces/services/creatives'
import CreativeActionButtons from '../../components/CreativeActionButtons'
import FormatsetInformationModal from '../../components/FormatsetInformationModal'
import { translateWithUserLocale } from '../../utils/i18n'

import { Box, Icon, Typography, styled } from '@design/pt-ui'
const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5),
  textAlign: 'left',
  marginLeft: theme.spacing(2.5),
}))

const Row = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'wrap',
  lineHeight: '28px',
})

const EditableTitle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  fontWeight: 600,
  fontSize: theme.typography.h5.fontSize,
}))

interface Props {
  creative: Creative | null
  creativeName: string
  error: string | null
  validating: boolean
  validation: IFormValidation
  isValidationDone: boolean
  dirty: boolean
  blueprint: FormBlueprint | null
  doCreativeAction: (creative: Creative, action: CreativeAction) => void
  setCreativeComment: (creativeId: CreativeId, comment: string) => any
  onSave: () => any
  onFinalize: () => any
  onValidate: () => any
  onNameChanged: (e: string) => any
}

function Header(props: Props) {
  const onBreadcrumbClick = (url: string) => {
    const history = DI.get('history')
    history.push(url)
  }

  const creativeService = DI.get('CreativeService') as ICreativeService
  const creative = props.creative

  if (creative == null) {
    return null
  }

  return (
    <Root>
      <Box
        sx={{
          display: 'flex',
          flexFlow: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          marginBottom: 2,
        }}>
        <Box sx={{ marginBottom: 1 }}>
          <Breadcrumb separatorText={<Icon>chevron_right</Icon>}>
            <BreadcrumbItem
              id="cp-breadcrumb-projects"
              sx={{ pl: 0 }}
              label={translateWithUserLocale('projects_overview')}
              href="/projects"
              onClick={onBreadcrumbClick}
            />
            {!creative.project ? (
              <BreadcrumbItem
                id="cp-breadcrumb-projects"
                label=""
                href="/projects"
                onClick={onBreadcrumbClick}
              />
            ) : (
              <BreadcrumbItem
                id="cp-breadcrumb-project"
                label={creative.project.name}
                labelLength={32}
                href={`/project/${creative.project.id}`}
                onClick={onBreadcrumbClick}
              />
            )}
            <BreadcrumbItem
              id="cp-breadcrumb-creative"
              label={creative.name}
              active={true}
              labelLength={32}
            />
          </Breadcrumb>
        </Box>
        <Row>
          <EditableTitle>
            <Editable
              value={
                props.creativeName.length ? props.creativeName : creative.name
              }
              onSave={
                creativeService.canEdit(creative)
                  ? props.onNameChanged
                  : undefined
              }
              saveOnBlur={true}
              maxLength={255}
            />
          </EditableTitle>
        </Row>
        {props.error ? (
          <Box
            sx={{
              color: 'error.light',
              marginTop: '10px',
              fontSize: 14,
              border: '1px solid',
              borderColor: 'error.light',
              borderRadius: 1,
              padding: '3px 5px',
              marginLeft: '15px',
            }}>
            <span>{props.error}</span>
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 2,
          alignItems: 'center',
        }}>
        <Box sx={{ display: 'flex', flexFlow: 'column', flex: 1 }}>
          <Row>
            {creative.project ? (
              <FormatsetInformationModal
                formatset={creative.project.formatsets[0]}
              />
            ) : null}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                paddingRight: 1,
                paddingLeft: 1,
                fontSize: 16,
                fontWeight: 600,
              }}>
              <Typography variant="subtitle1">
                {translateWithUserLocale('creative_details.creative_type')}{' '}
                {props.creative ? props.creative.type.label : ''}
              </Typography>
            </Box>
            <HistoryTag
              title={translateWithUserLocale('history.sent_to_agency')}
              events={creative.sent}
              icon="send"
              iconColor="action"
            />
            <HistoryTag
              title={translateWithUserLocale(
                'history.reopened_after_sent_to_agency',
              )}
              events={creative.reopens}
              icon="local_offer"
              iconColor="action"
            />
            <HistoryTag
              title={translateWithUserLocale('history.outdated')}
              events={creative.outdated}
              icon="timelapse"
              iconColor="secondary"
            />
          </Row>
        </Box>
        <CreativeActionButtons
          creative={creative}
          validating={props.validating}
          validation={props.validation}
          isValidationDone={props.isValidationDone}
          dirty={props.dirty}
          blueprint={props.blueprint}
          doCreativeAction={props.doCreativeAction}
          onSave={props.onSave}
          onFinalize={props.onFinalize}
          onValidate={props.onValidate}
        />
      </Box>
    </Root>
  )
}

const mapStateToProps = (state: State) => {
  return {}
}
export default connect(mapStateToProps)(
  Header as any,
) as React.ComponentType<any>
